import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Zap, Droplets, Layers, Grid, Truck, Gem, Brush, Stamp, RotateCcw, AlertTriangle } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/Card';
type QuizPhase = 'quiz' | 'contact' | 'result';
type Recommendation = {
  system: {
    type: string;
    icon: any;
    reason: string;
  };
  material: {
    type: string;
    icon: any;
    reason: string;
  };
  finish: {
    type: string;
    icon: any;
    reason: string;
  } | null;
  warning?: string;
};
const steps = [{
  id: 'application',
  question: 'What are you looking to heat?',
  options: [{
    label: 'Driveway',
    value: 'driveway'
  }, {
    label: 'Walkway or Steps',
    value: 'walkway'
  }, {
    label: 'Patio',
    value: 'patio'
  }, {
    label: 'Multiple Areas',
    value: 'multiple'
  }]
}, {
  id: 'areaSize',
  question: 'Roughly how big is the area?',
  options: [{
    label: 'Small (under 400 sq ft)',
    value: 'small'
  }, {
    label: 'Medium (400–800 sq ft)',
    value: 'medium'
  }, {
    label: 'Large (800–1,500 sq ft)',
    value: 'large'
  }, {
    label: 'Very Large (1,500+ sq ft)',
    value: 'very-large'
  }]
}, {
  id: 'slope',
  question: 'Is there a slope?',
  options: [{
    label: 'Flat',
    value: 'flat'
  }, {
    label: 'Slight slope',
    value: 'slight'
  }, {
    label: 'Steep slope',
    value: 'steep'
  }]
}, {
  id: 'appearance',
  question: 'How important is appearance?',
  options: [{
    label: 'Function first — just make it work',
    value: 'function'
  }, {
    label: 'Nice but practical',
    value: 'practical'
  }, {
    label: 'I want it to look amazing',
    value: 'amazing'
  }]
}, {
  id: 'priority',
  question: 'What is your top priority?',
  options: [{
    label: 'Lowest upfront cost',
    value: 'cost'
  }, {
    label: 'Longest lifespan & lowest maintenance',
    value: 'lifespan'
  }, {
    label: 'Best appearance',
    value: 'appearance'
  }, {
    label: 'Fastest installation',
    value: 'speed'
  }]
}, {
  id: 'construction',
  question: 'Is this new construction or replacing existing?',
  options: [{
    label: 'New construction',
    value: 'new'
  }, {
    label: 'Replacing existing surface',
    value: 'replace'
  }, {
    label: 'Adding to existing (expansion)',
    value: 'expand'
  }, {
    label: 'Not sure',
    value: 'unsure'
  }]
}, {
  id: 'saltUsage',
  question: 'Do you currently use salt or de-icer?',
  options: [{
    label: 'Yes, heavily',
    value: 'heavy'
  }, {
    label: 'Sometimes',
    value: 'moderate'
  }, {
    label: 'Rarely or never',
    value: 'rare'
  }, {
    label: 'Not sure',
    value: 'unsure'
  }]
}, {
  id: 'budget',
  question: 'Budget comfort level?',
  options: [{
    label: 'Keep it practical',
    value: 'practical'
  }, {
    label: 'Mid-range is fine',
    value: 'mid'
  }, {
    label: 'I want the best option available',
    value: 'high'
  }]
}];
export function QuoteQuiz() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<QuizPhase>('quiz');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const handleOptionSelect = (value: string) => {
    const stepId = steps[currentStep].id;
    const newAnswers = {
      ...answers,
      [stepId]: value
    };
    setAnswers(newAnswers);
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      calculateRecommendation(newAnswers);
      setPhase('contact');
    }
  };
  const calculateRecommendation = (finalAnswers: Record<string, string>) => {
    let system = {
      type: 'Electric System',
      icon: Zap,
      reason: 'Best for standard residential projects with fastest response time.'
    };
    let material = {
      type: 'Concrete (Poured)',
      icon: Layers,
      reason: 'Most durable and versatile option for heated driveways.'
    };
    let finish: {
      type: string;
      icon: any;
      reason: string;
    } | null = {
      type: 'Exposed Aggregate',
      icon: Gem,
      reason: 'Excellent traction and hides salt stains well.'
    };
    let warning = '';
    // System Logic
    if (finalAnswers.areaSize === 'very-large' || finalAnswers.areaSize === 'large' && finalAnswers.priority === 'lifespan') {
      system = {
        type: 'Hydronic System',
        icon: Droplets,
        reason: 'More efficient operating costs for large surface areas.'
      };
    }
    // Material Logic
    if (finalAnswers.application === 'multiple') {
      material = {
        type: 'Custom Assessment',
        icon: Layers,
        reason: 'We will recommend specific materials for each area during your site visit.'
      };
    } else if (finalAnswers.application === 'driveway') {
      if (finalAnswers.priority === 'cost') {
        material = {
          type: 'Asphalt',
          icon: Truck,
          reason: 'Lowest upfront cost option.'
        };
        finish = null;
      } else if (finalAnswers.priority === 'appearance') {
        material = {
          type: 'Interlock Pavers',
          icon: Grid,
          reason: 'Best curb appeal and design flexibility.'
        };
        finish = null;
      }
    } else if (finalAnswers.application === 'patio') {
      if (finalAnswers.appearance === 'amazing') {
        if (finalAnswers.budget === 'high') {
          material = {
            type: 'Natural Stone',
            icon: Gem,
            reason: 'Premium look for high-end patios.'
          };
          finish = null;
        } else {
          material = {
            type: 'Interlock Pavers',
            icon: Grid,
            reason: 'Great balance of aesthetics and durability.'
          };
          finish = null;
        }
      }
    }
    // Finish Logic (only if concrete)
    if (material.type.includes('Concrete')) {
      if (finalAnswers.slope === 'steep') {
        finish = {
          type: 'Broom Finish',
          icon: Brush,
          reason: 'Maximum traction required for safety on steep slopes.'
        };
        warning = 'Safety Note: Due to the steep slope, we strongly recommend a high-traction broom finish.';
      } else if (finalAnswers.appearance === 'function') {
        finish = {
          type: 'Broom Finish',
          icon: Brush,
          reason: 'Simple, effective, and budget-friendly.'
        };
      } else if (finalAnswers.appearance === 'amazing' && finalAnswers.application === 'patio') {
        finish = {
          type: 'Stamped Concrete',
          icon: Stamp,
          reason: 'Decorative look with texture additive for grip.'
        };
      }
    }
    setRecommendation({
      system,
      material,
      finish,
      warning
    });
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    const contactData = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      phone: formData.get('phone'),
      email: formData.get('email'),
      address: formData.get('address'),
      city: formData.get('city'),
      message: formData.get('message')
    };
    const payload = {
      ...answers,
      ...contactData,
      recommendedSystem: recommendation?.system.type,
      recommendedMaterial: recommendation?.material.type,
      recommendedFinish: recommendation?.finish?.type || 'N/A'
    };
    try {
      await fetch('https://webhook-processor-production-1b6a.up.railway.app/webhook/45e684f1-b972-41c9-8ce5-cee0fc3dbf7a', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      setPhase('result');
    } catch (error) {
      console.log('Submission error:', error);
      setPhase('result'); // Show result anyway for UX
    } finally {
      setIsSubmitting(false);
    }
  };
  const resetQuiz = () => {
    setCurrentStep(0);
    setAnswers({});
    setPhase('quiz');
    setRecommendation(null);
  };
  // Progress calculation
  const progress = phase === 'quiz' ? (currentStep + 1) / steps.length * 100 : 100;
  return <Card className="shadow-xl border-primary/10 overflow-hidden bg-white" data-id="element-839">
      {/* Progress Bar */}
      <div className="h-2 w-full bg-slate-100" data-id="element-840">
        <div className="h-full bg-primary transition-all duration-500 ease-out" style={{
        width: `${progress}%`
      }} data-id="element-841"></div>
      </div>

      {phase === 'quiz' && <CardContent className="p-6 md:p-8 animate-in fade-in slide-in-from-right-4 duration-300" key={currentStep} data-id="element-842">
          {currentStep === 0 && <div className="text-center mb-6 pb-6 border-b border-slate-100" data-id="element-843">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-844">
                Get Your Personalized Recommendation & Free Quote
              </h3>
              <p className="text-sm text-muted-foreground mt-1" data-id="element-845">
                Answer 8 quick questions and we'll match you with the ideal
                system, material, and finish.
              </p>
            </div>}
          <span className="text-xs font-bold text-primary uppercase tracking-wider mb-3 block" data-id="element-846">
            Question {currentStep + 1} of {steps.length}
          </span>
          <h3 className="text-2xl font-bold text-slate-900 mb-8" data-id="element-847">
            {steps[currentStep].question}
          </h3>

          <div className="grid gap-3" data-id="element-848">
            {steps[currentStep].options.map(option => <button key={option.value} onClick={() => handleOptionSelect(option.value)} className="text-left p-4 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all duration-200 flex items-center justify-between group" data-id="element-849">
                <span className="font-medium text-slate-700 group-hover:text-primary" data-id="element-850">
                  {option.label}
                </span>
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-primary opacity-0 group-hover:opacity-100 transition-all" data-id="element-851" />
              </button>)}
          </div>
        </CardContent>}

      {phase === 'contact' && <div className="animate-in fade-in zoom-in-95 duration-300" data-id="element-852">
          <CardHeader className="bg-primary/5 border-b border-primary/10" data-id="element-853">
            <CardTitle data-id="element-854">Almost There! Enter Your Details</CardTitle>
            <CardDescription data-id="element-855">
              We'll show your recommendation instantly and email you a detailed
              breakdown.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6" data-id="element-856">
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-857">
              <div className="grid sm:grid-cols-2 gap-4" data-id="element-858">
                <Input id="firstName" name="firstName" label="First Name" placeholder="John" required data-id="element-859" />
                <Input id="lastName" name="lastName" label="Last Name" placeholder="Doe" required data-id="element-860" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4" data-id="element-861">
                <Input id="phone" name="phone" label="Phone Number" type="tel" placeholder="(647) 706-9908" required data-id="element-862" />
                <Input id="email" name="email" label="Email Address" type="email" placeholder="john@example.com" required data-id="element-863" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4" data-id="element-864">
                <Input id="address" name="address" label="Address" placeholder="123 Maple Ave" required data-id="element-865" />
                <Input id="city" name="city" label="City" placeholder="Toronto" required data-id="element-866" />
              </div>
              <div className="space-y-2" data-id="element-867">
                <label htmlFor="message" className="block text-sm font-medium text-foreground" data-id="element-868">
                  Project Details (Optional)
                </label>
                <textarea id="message" name="message" rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" placeholder="Tell us about your driveway size or specific needs..." data-id="element-869"></textarea>
              </div>
              <Button type="submit" fullWidth size="lg" disabled={isSubmitting} data-id="element-870">
                {isSubmitting ? 'Calculating...' : 'See My Personalized Recommendation'}
              </Button>
              <p className="text-xs text-center text-muted-foreground" data-id="element-871">
                Your information is secure. We never share your data with third
                parties.
              </p>
            </form>
          </CardContent>
        </div>}

      {phase === 'result' && recommendation && <div className="animate-in fade-in zoom-in-95 duration-500 p-6 md:p-8 text-center" data-id="element-872">
          <div className="mb-8" data-id="element-873">
            <div className="h-16 w-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-874">
              <CheckCircle2 className="h-8 w-8" data-id="element-875" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900" data-id="element-876">
              Recommendation Ready!
            </h3>
            <p className="text-slate-600 mt-2" data-id="element-877">
              Based on your answers, here is the ideal setup for your property.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8 text-left" data-id="element-878">
            {/* System Card */}
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 relative overflow-hidden" data-id="element-879">
              <div className="absolute top-0 left-0 w-full h-1 bg-blue-500" data-id="element-880"></div>
              <div className="flex items-center gap-2 mb-2 text-blue-700 font-bold text-sm uppercase tracking-wide" data-id="element-881">
                <recommendation.system.icon className="h-4 w-4" data-id="element-882" /> System Type
              </div>
              <h4 className="font-bold text-lg text-slate-900 mb-1" data-id="element-883">
                {recommendation.system.type}
              </h4>
              <p className="text-xs text-slate-600" data-id="element-884">
                {recommendation.system.reason}
              </p>
            </div>

            {/* Material Card */}
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 relative overflow-hidden" data-id="element-885">
              <div className="absolute top-0 left-0 w-full h-1 bg-amber-500" data-id="element-886"></div>
              <div className="flex items-center gap-2 mb-2 text-amber-700 font-bold text-sm uppercase tracking-wide" data-id="element-887">
                <recommendation.material.icon className="h-4 w-4" data-id="element-888" /> Material
              </div>
              <h4 className="font-bold text-lg text-slate-900 mb-1" data-id="element-889">
                {recommendation.material.type}
              </h4>
              <p className="text-xs text-slate-600" data-id="element-890">
                {recommendation.material.reason}
              </p>
            </div>

            {/* Finish Card */}
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 relative overflow-hidden" data-id="element-891">
              <div className="absolute top-0 left-0 w-full h-1 bg-purple-500" data-id="element-892"></div>
              <div className="flex items-center gap-2 mb-2 text-purple-700 font-bold text-sm uppercase tracking-wide" data-id="element-893">
                {recommendation.finish ? <recommendation.finish.icon className="h-4 w-4" data-id="element-894" /> : <Layers className="h-4 w-4" data-id="element-895" />}{' '}
                Finish
              </div>
              <h4 className="font-bold text-lg text-slate-900 mb-1" data-id="element-896">
                {recommendation.finish ? recommendation.finish.type : 'N/A'}
              </h4>
              <p className="text-xs text-slate-600" data-id="element-897">
                {recommendation.finish ? recommendation.finish.reason : 'Finish selection applies to concrete only.'}
              </p>
            </div>
          </div>

          {recommendation.warning && <div className="bg-amber-50 border border-amber-100 p-4 rounded-lg mb-8 text-left flex gap-3 items-start" data-id="element-898">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" data-id="element-899" />
              <p className="text-sm text-amber-800 font-medium" data-id="element-900">
                {recommendation.warning}
              </p>
            </div>}

          <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm mb-8" data-id="element-901">
            <h4 className="font-semibold text-slate-900 mb-4" data-id="element-902">
              What happens next?
            </h4>
            <div className="space-y-3 text-left max-w-xs mx-auto" data-id="element-903">
              <div className="flex items-center gap-3 text-sm text-slate-600" data-id="element-904">
                <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" data-id="element-905" />
                <span data-id="element-906">We'll contact you within 24 hours</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-600" data-id="element-907">
                <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" data-id="element-908" />
                <span data-id="element-909">Schedule a brief site visit</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-600" data-id="element-910">
                <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" data-id="element-911" />
                <span data-id="element-912">Receive your detailed written quote</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3" data-id="element-913">
            <p className="text-sm text-slate-500" data-id="element-914">
              Questions? Call us at{' '}
              <a href="tel:647-706-9908" className="text-primary font-bold hover:underline" data-id="element-915">
                (647) 706-9908
              </a>
            </p>
            <Button variant="outline" onClick={resetQuiz} className="mx-auto" data-id="element-916">
              <RotateCcw className="h-4 w-4 mr-2" data-id="element-917" /> Start Over
            </Button>
          </div>
        </div>}
    </Card>;
}